// store/modules/sms.js
import { initAliyunCaptcha } from '@/utils/aliyunCaptcha'
import { sendSmsCode } from '@/api/sms'

// 常量
const COUNTDOWN_SECONDS = 60
const MOBILE_REGEX = /^1[3-9]\d{9}$/

const state = {
  mobile: '',
  countdown: 0,
  captchaInstance: null,
  captchaMode: 'popup', // popup 或 embed
  isLoading: false,
  countdownTimer: null
}

const getters = {
  canSendSms: state => state.countdown === 0 && !state.isLoading,

  countdownText: state => {
    if (state.isLoading) return '发送中...'
    return state.countdown > 0 ? `${state.countdown}秒后重试` : '发送验证码'
  },

  isMobileValid: state => MOBILE_REGEX.test(state.mobile)
}

const mutations = {
  SET_COUNTDOWN(state, value) {
    state.countdown = Math.max(0, value)
  },

  SET_MOBILE(state, value) {
    state.mobile = value
  },

  SET_CAPTCHA_INSTANCE(state, instance) {
    state.captchaInstance = instance
  },

  SET_CAPTCHA_MODE(state, mode) {
    state.captchaMode = mode
  },

  SET_LOADING(state, value) {
    state.isLoading = value
  },

  SET_COUNTDOWN_TIMER(state, timer) {
    // 清除旧的定时器
    if (state.countdownTimer) {
      clearInterval(state.countdownTimer)
    }
    state.countdownTimer = timer
  },

  DECREASE_COUNTDOWN(state) {
    if (state.countdown > 0) {
      state.countdown--
    }
  },

  CLEAR_COUNTDOWN_TIMER(state) {
    if (state.countdownTimer) {
      clearInterval(state.countdownTimer)
      state.countdownTimer = null
    }
  },

  RESET_STATE(state) {
    state.mobile = ''
    state.countdown = 0
    state.isLoading = false
    if (state.countdownTimer) {
      clearInterval(state.countdownTimer)
      state.countdownTimer = null
    }
  }
}

const actions = {
  /**
   * 初始化并触发阿里云验证码
   */
  async triggerCaptcha({ commit, dispatch, state, getters }, options = {}) {
    // 前置验证
    if (state.countdown > 0) {
      return Promise.reject(new Error(`请等待 ${state.countdown} 秒后再试`))
    }

    if (state.isLoading) {
      return Promise.reject(new Error('正在发送中，请稍候...'))
    }

    if (!getters.isMobileValid) {
      return Promise.reject(new Error('请输入正确的手机号码'))
    }

    commit('SET_LOADING', true)

    try {
      const result = await initCaptchaFlow({ commit, dispatch, state }, options)
      return result
    } catch (error) {
      commit('SET_LOADING', false)

      throw error
    }
  },

  /**
   * 发送短信验证码
   */
  async sendSmsCode({ state, commit, dispatch }, captchaVerifyParam) {
    try {
      const response = await sendSmsCode({
        mobile: state.mobile,
        captchaVerifyParam,
        type: 'aliyun',
        sceneId: '1r0wupk8'
      })

      if (response.status) {
        // 开始倒计时
        commit('SET_COUNTDOWN', COUNTDOWN_SECONDS)
        dispatch('startCountdown')
        commit('SET_LOADING', false)

        return {
          success: true,
          message: response.errMsg || '验证码已发送，请注意查收'
        }
      } else {
        throw new Error(response.errMsg || '发送失败，请稍后重试')
      }
    } catch (error) {
      commit('SET_LOADING', false)
      console.error('发送短信失败:', error)
      throw error
    }
  },

  /**
   * 开始倒计时
   */
  startCountdown({ commit, state }) {
    const timer = setInterval(() => {
      commit('DECREASE_COUNTDOWN')

      if (state.countdown === 0) {
        commit('CLEAR_COUNTDOWN_TIMER')
      }
    }, 1000)

    commit('SET_COUNTDOWN_TIMER', timer)
  },

  /**
   * 手动显示验证码（用于已初始化的实例）
   */
  showCaptcha({ state }) {
    if (state.captchaInstance?.show) {
      state.captchaInstance.show()
    } else {
      throw new Error('验证码未初始化，请先调用 triggerCaptcha')
    }
  },

  /**
   * 重置状态
   */
  resetState({ commit }) {
    commit('RESET_STATE')
  }
}

/**
 * 初始化验证码流程
 * @private
 */
async function initCaptchaFlow({ commit, dispatch, state }, options) {
  return new Promise((resolve, reject) => {
    initAliyunCaptcha({
      mode: state.captchaMode,
      element: options.element,
      button: options.button,

      onSuccess: async captchaVerifyParam => {
        try {
          const result = await dispatch('sendSmsCode', captchaVerifyParam)
          options.onSuccess?.(result)
          resolve(result)
        } catch (error) {
          console.error('发送短信失败:', error)
          commit('SET_LOADING', false)
          reject(error)
        }
      },

      onFail: result => {
        commit('SET_LOADING', false)
        reject(new Error('验证失败，请重试'))
      },

      onGetInstance: instance => {
        commit('SET_CAPTCHA_INSTANCE', instance)
        resolve(instance)
      },

      onClose: () => {
        commit('SET_LOADING', false)
        options.onClose?.()
        reject()
      },

      onError: error => {
        commit('SET_LOADING', false)
        options.onError?.(error)
        reject(error)
      }
    }).catch(error => {
      commit('SET_LOADING', false)
      reject(error)
    })
  })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
